import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { PortRequestFormServiceService } from '../../service/port-request-FormService/port-request-form.service.service';

interface Report {
  id: number;
  vesselIMO: string;
  vesselName: string;
  jsonCreatedAt: string;
  emailReceivedAt?: string;   // ✅ added
  event: string;
  version?: string;           // ✅ added
  disabled?: boolean;         // ✅ FIX ERROR
  error_mail_drafted?: boolean;
  error_mail_drafted_at?: string | null;
}

interface Vessel {
  vesselName: string;
  vesselIMO: string;
  reports: Report[];
  showReports?: boolean;
}

@Component({
  selector: 'app-report-verify-popup',
  templateUrl: './report-verify-popup.component.html',
  styleUrls: ['./report-verify-popup.component.scss'],
})
export class ReportVerifyPopupComponent implements OnInit {
  reportTypeForm!: FormGroup;

  vessels: Vessel[] = [];  
  filteredVessels: Vessel[] = [];

  selectedReport: Report | null = null;
  searchControl = new FormControl('');

  isLoading = false;
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private portRequestFormService: PortRequestFormServiceService,
    private router: Router,
    private dialogRef: MatDialogRef<ReportVerifyPopupComponent>,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.fetchReportTypes();

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => this.applyFilter(value || ''));
  }

  initForm(): void {
    this.reportTypeForm = this.fb.group({});
  }

  fetchReportTypes(): void {
    this.isLoading = true;

    this.portRequestFormService.getReportVerify().subscribe({
      next: (response: any) => {
        this.isLoading = false;

        console.log("API RESPONSE:", response);

        let vesselsData: any[] = [];

        // ✅ CASE 1: API returns { vessels: [...] }
        if (response?.vessels) {
          vesselsData = response.vessels.map((v: any) => ({
            vesselName: v.vesselName,
            vesselIMO: v.vesselIMO,
            reports: v.reports || []
          }));
        }

        // ✅ CASE 2: API returns direct array []
        else if (Array.isArray(response)) {

          const vesselMap: Record<string, any> = {};

          response.forEach((report: any) => {
            const imo = report.vesselIMO;

            if (!vesselMap[imo]) {
              vesselMap[imo] = {
                vesselName: report.vesselName,
                vesselIMO: report.vesselIMO,
                reports: []
              };
            }

            vesselMap[imo].reports.push(report);
          });

          vesselsData = Object.values(vesselMap);
        }

        // ❌ no data
        if (!vesselsData.length) {
          this.vessels = [];
          this.filteredVessels = [];
          return;
        }

        // ✅ FINAL MAPPING
        this.vessels = vesselsData.map((v: any) => {

          const sortedReports = (v.reports || []).map((report: any) => ({
            id: report.id,
            vesselName: report.vesselName,
            vesselIMO: report.vesselIMO,
            jsonCreatedAt: report.jsonCreatedAt,
            emailReceivedAt: report.emailReceivedAt ? (report.emailReceivedAt.includes('Z') || report.emailReceivedAt.includes('+') ? report.emailReceivedAt : report.emailReceivedAt + 'Z') : report.emailReceivedAt,
            event: report.eventsList || 'NA',
            version: '2.2',
            disabled: false,
            error_mail_drafted: report.error_mail_drafted || false,
            error_mail_drafted_at: report.error_mail_drafted_at || null
          }));

          // 🔥 latest first
          sortedReports.sort(
            (a: any, b: any) =>
              new Date(a.jsonCreatedAt).getTime() -
              new Date(b.jsonCreatedAt).getTime()
          );

          // 🔥 only latest enabled
          sortedReports.forEach((r: any, i: number) => {
            r.disabled = i !== 0;
          });

          return {
            vesselName: v.vesselName,
            vesselIMO: v.vesselIMO,
            reports: sortedReports,
            showReports: false
          };
        });

        // open first
        if (this.vessels.length) {
          this.vessels[0].showReports = true;
        }

        this.filteredVessels = [...this.vessels];

        console.log("FINAL UI DATA:", this.filteredVessels);

        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      },

      error: (err) => {
        this.isLoading = false;
        console.error('Error fetching reports:', err);
        this.vessels = [];
        this.filteredVessels = [];
      },
    });
  }

  applyFilter(search: string): void {
    const term = search.toLowerCase().trim();

    if (!term) {
      this.filteredVessels = [...this.vessels];
      return;
    }

    this.filteredVessels = this.vessels.filter((v) =>
      v.vesselName.toLowerCase().includes(term),
    );

    this.filteredVessels.forEach((v) => (v.showReports = false));
    if (this.filteredVessels.length === 1) {
      this.filteredVessels[0].showReports = true;
    }
  }

  toggleDropdown(selected: Vessel): void {
    this.filteredVessels.forEach((v) => {
      if (v !== selected) v.showReports = false;
    });
    selected.showReports = !selected.showReports;
  }

  onRadioChange(report: Report): void {

    this.selectedReport = report;

  }

  onSubmit(): void {
    if (!this.selectedReport || this.isSubmitting) return;

    this.isSubmitting = true;

    this.portRequestFormService
      .getPendingReportByIMO(
        this.selectedReport.vesselIMO,
        this.selectedReport.id
      )
      .subscribe({
        next: (data) => {
          this.isSubmitting = false;
          this.portRequestFormService.setReportData(data);
          this.dialogRef.close(true);
          this.router.navigate(['/pc-newForm']);
        },
        error: (err) => {
          this.isSubmitting = false;
          console.error('Submit error:', err);
        },
      });
  }


  closeModal(): void {
    this.dialogRef.close();
  }
}
